import _ from 'underscore';
import $ from 'jquery';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { ScopeSelector } from '@bingads-webui-react/scope-selector';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';

// eslint-disable-next-line
import { bingadsGridColumn } from '@bingads-webui-component-legacy/bingads-grid/shared/column';
// eslint-disable-next-line
import gridRowTemplate from '@bingads-webui-campaign-legacy/adextension-callout/template/gridRowTemplate.pug';

import { showRejectionReason, hideRejectionReason } from './grid-popover';
import { getCalloutExtensionStyles } from '../../../style';
import { getToolbarConfig } from './toolbar-config';
import { getAssociationConfigs } from './association-config';
import {

  createSubAccountLevelCalloutExtensionGrid,
  createHotelGroupLevelCalloutExtensionGrid
} from './grid-factory';

const createCalloutExtensionScopeOptions = ({ i18n }) => [{
  name: i18n.getString(_TL_('Sub account')),
  value: 'SubAccount',
  associationType: 4,
},
{
  name: i18n.getString(_TL_('Hotel group')),
  value: 'HotelGroup',
  associationType: 5,
}];

const createGridColumns = ({ i18n }) => [
  bingadsGridColumn({
    name: 'AdExtension',
    property: item => (item.isAggregateRow ? item.AdExtension : _.pick(item.AdExtension, 'Text')),
    template: (local) => {
      if (_.isEmpty(local.value)) {
        return '<span/>';
      } else if (typeof local.value !== 'object') {
        return local.value;
      }
      return gridRowTemplate({
        data: {
          Text: local.value.Text,
        },
      });
    },
    type: 'string',
    sortable: false,
    title: i18n.getString(_TL_('Extension')),
    helpId: 'Page_CampaignGrids_AdExtensions', // TODO: Update with Hotel Center help id
    helpAriaLabelTextTemplate: `${i18n.getString(_TL_('Click to learn more about'))} ${i18n.getString(_TL_('Extension'))}`,
    bodyClasses: ['col-AdExtension'],
  }),
];

class HotelCenterCalloutExtensionPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.scopeOptions = createCalloutExtensionScopeOptions({ i18n: props.i18n });
    this.i18nHotelCenter = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');

    this.state = {
      backboneGridView: null,
      scopeIndex: 0,
    };

    this.createGrid();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.scopeIndex !== this.state.scopeIndex) {
      this.createGrid();
    }
  }

  componentWillUnmount() {
    if (this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
  }

  onScopeChange = (index) => {
    this.setState({ scopeIndex: index });
  }

  onBeginEditAssociation = (editorOptionsFromGrid) => {
    const { showTopOfGridPanel } = this.props;
    const { containerConfig, editorConfig } = getAssociationConfigs(editorOptionsFromGrid, { i18nHotelCenter: this.i18nHotelCenter });

    import(/* webpackMode: 'eager' */'@bingads-webui-campaign-legacy/adextension-associations-container/view/index')
      .then((AssociationContainer) => {
        const AssociationContainerView = AssociationContainer.default;
        const editorViewInstance = new AssociationContainerView(containerConfig, editorConfig).render();

        editorViewInstance.on('cancel', () => {
          editorViewInstance.remove();
          this.props.showTopOfGridPanel(null);
          this.refreshGrid();
        });

        editorViewInstance.on('save', () => {
          editorViewInstance.remove();
          this.props.showTopOfGridPanel(null);
          this.refreshGrid();
        });
        const topEditorPanel = (
          <div className={this.props.classes.CalloutExtensionScopeContainer}>
            <BackboneInstanceWrapper instance={editorViewInstance} managed />
          </div>
        );

        showTopOfGridPanel(topEditorPanel);
      });
  }

  createGrid() {
    const {
      i18n,
      perfMarker,
      onGridInjected,
      toolbarPlugin,
      serverContext,
      scope,
      permissions,
      preferenceService,
    } = this.props;

    if (this.state && this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
    if (this.state) {
      this.setState({ backboneGridView: null });
    }

    const columns = createGridColumns({ i18n });
    const scopeSelection = this.scopeOptions[this.state.scopeIndex];

    const gridParams = {
      createAssociationEditorForUiNext: (options) => { this.onBeginEditAssociation(options); },
      toolbarPlugin,
      columns,
      i18n,
      perfMarker,
      serverContext,
      scope,
      permissions,
      preferenceService,
    };

    const backboneGridViewPromise = scopeSelection.value === 'SubAccount' ?
      createSubAccountLevelCalloutExtensionGrid(gridParams) :
      createHotelGroupLevelCalloutExtensionGrid(gridParams);

    const CalloutExtensionGridView = React.lazy(() =>
      backboneGridViewPromise.then((injectedView) => {
        const { gridView } = injectedView.grid;

        this.installDeliveryStatusReactor(gridView);
        gridView.on('didUpdate', () => { this.installDeliveryStatusReactor(gridView); });

        onGridInjected({
          backboneGridView: injectedView,
          deselectAll: _.isFunction(gridView.deselectAll) ? gridView.deselectAll.bind(gridView) : undefined,
        });

        this.setState({ backboneGridView: injectedView });

        return ({
          default: () => (<StyledGridViewWrapper gridView={injectedView} />),
        });
      }).catch(err => err));

    this.calloutExtensionGridView = (<CalloutExtensionGridView />);
  }

  refreshGrid = () => {
    if (this.state.backboneGridView) {
      this.state.backboneGridView.grid.gridView.refresh(true);
    }
  }

  installDeliveryStatusReactor = (gridView) => {
    if ($('.delivery-info').length > 0) {
      $('.trigger-popup').mouseenter(event => this.handleTouchingDeliveryStatusRejectionReason(event, gridView));
      $('.delivery-info, .grid-table, .table-page-container').mouseleave(this.handleLeavingDeliveryStatusRejectionReason);
    }
  }

  handleTouchingDeliveryStatusRejectionReason = (event, gridView) => {
    event.stopPropagation();

    const { scope, i18n } = this.props;
    const key = gridView.keyOfElement(event.target);
    const model = gridView.itemWithKey(key);
    const scopeSelection = this.scopeOptions[this.state.scopeIndex];

    if (model) {
      showRejectionReason(event, model, scope, 'CalloutAdExtension', scopeSelection.associationType, i18n);
    }
  };

  handleLeavingDeliveryStatusRejectionReason = (event) => {
    if ($(event.relatedTarget).closest('#popover-basic').length === 0) {
      hideRejectionReason();
    }
  };

  render() {
    const {
      panelView,
      renderToolbarContainer,
      showTopGridPanel,
    } = this.props;

    const topGridPanel = showTopGridPanel ? (<TopGridPanel panelView={panelView} />) : null;

    const topOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <React.Fragment>
          {topGridPanel}
        </React.Fragment>
      </React.Suspense>
    );

    const stickyTopOfGridContent = (
      <React.Fragment>
        {renderToolbarContainer()}
      </React.Fragment>
    );

    const scopeSelector = (
      <ScopeSelector
        pillControlParams={{
          levelIndex: this.state.scopeIndex,
          onItemChange: ({ index }) => this.onScopeChange(index),
          levelItems: this.scopeOptions,
        }}
      />
    );

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <div className={classNames(['callout-extension-scope-container', showTopGridPanel ? 'hide' : ''])}>
          {scopeSelector}
        </div>
        <StyledTablePageContainer
          disableFooter
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.calloutExtensionGridView}
          backboneGridView={this.state.backboneGridView}
          customClasses={{
            tablePageContainer: `callout-extension-table-page-container ${this.props.classes.CalloutExtension}`,
            gridViewContainer: 'callout-extension-grid-container',
          }}
          scrollToTop={this.props.forceHideToolbar && this.props.showTopGridPanel}
          forceResetScrollFlag={this.props.forceResetScrollFlag}
        />
      </React.Suspense>
    );
  }
}

HotelCenterCalloutExtensionPage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({}),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    CalloutExtension: PropTypes.string,
    CalloutExtensionScopeContainer: PropTypes.string,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  panelView: PropTypes.func.isRequired,
  onGridInjected: PropTypes.func.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  forceHideToolbar: PropTypes.bool.isRequired,
  forceResetScrollFlag: PropTypes.bool.isRequired,
  showTopOfGridPanel: PropTypes.func.isRequired,
};

export const StyledHotelCenterCalloutExtensionPage = withDefaultStyles(
  withTablePageStateManager({ getToolbarConfig })(HotelCenterCalloutExtensionPage),
  getCalloutExtensionStyles
);
